import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Modal, Form, Input, InputNumber, message, Switch, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { api } from '../services/api';

interface UnitSet {
  id: number;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: string;
}

interface Unit {
  id: number;
  unitSetId: number;
  unitCode: string;
  unitName: string;
  isBaseUnit: boolean;
  conversionFactor: number;
  isActive: boolean;
  createdAt: string;
}

const UnitSets: React.FC = () => {
  const [unitSets, setUnitSets] = useState<UnitSet[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [unitModalVisible, setUnitModalVisible] = useState(false);
  const [editingUnitSet, setEditingUnitSet] = useState<UnitSet | null>(null);
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null);
  const [selectedUnitSetId, setSelectedUnitSetId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const [unitForm] = Form.useForm();

  useEffect(() => {
    loadUnitSets();
    loadUnits();
  }, []);

  const loadUnitSets = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/unitsets');
      setUnitSets(data);
    } catch (error) {
      message.error('Birim setleri yüklenirken hata oluştu');
      console.error('UnitSets load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnits = async () => {
    try {
      const { data } = await api.get('/units');
      const mappedUnits = data.map((unit: any) => ({
        id: unit.id,
        unitSetId: unit.unitSetId,
        unitCode: unit.unitCode || unit.symbol,
        unitName: unit.unitName || unit.name,
        isBaseUnit: unit.isBaseUnit !== undefined ? unit.isBaseUnit : (unit.baseRatio === 1),
        conversionFactor: unit.conversionFactor || unit.baseRatio,
        isActive: unit.isActive !== undefined ? unit.isActive : true,
        createdAt: unit.createdAt || ''
      }));
      setUnits(mappedUnits);
    } catch (error) {
      message.error('Birimler yüklenirken hata oluştu');
      console.error('Units load error:', error);
    }
  };

  const handleAddUnitSet = () => {
    setEditingUnitSet(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditUnitSet = (unitSet: UnitSet) => {
    setEditingUnitSet(unitSet);
    form.setFieldsValue(unitSet);
    setModalVisible(true);
  };

  const handleAddUnit = (unitSetId: number) => {
    setSelectedUnitSetId(unitSetId);
    setEditingUnit(null);
    unitForm.resetFields();
    unitForm.setFieldsValue({ unitSetId });
    setUnitModalVisible(true);
  };

  const handleEditUnit = (unit: Unit) => {
    setEditingUnit(unit);
    setSelectedUnitSetId(unit.unitSetId);
    unitForm.setFieldsValue(unit);
    setUnitModalVisible(true);
  };

  const handleToggleActiveUnitSet = async (id: number, currentStatus: boolean) => {
    try {
      await api.patch(`/unitsets/${id}/toggle-active`);
      message.success(`Birim seti ${!currentStatus ? 'aktif' : 'pasif'} edildi`);
      loadUnitSets();
    } catch (error) {
      message.error('Durum değiştirilirken hata oluştu');
      console.error('Toggle error:', error);
    }
  };

  const handleToggleActiveUnit = async (id: number, currentStatus: boolean) => {
    try {
      await api.patch(`/units/${id}/toggle-active`);
      message.success(`Birim ${!currentStatus ? 'aktif' : 'pasif'} edildi`);
      loadUnits();
    } catch (error) {
      message.error('Durum değiştirilirken hata oluştu');
      console.error('Toggle error:', error);
    }
  };

  const handleDeleteUnitSet = async (id: number) => {
    try {
      setLoading(true);
      await api.delete(`/unitsets/${id}`);
      message.success('Birim seti silindi');
      loadUnitSets();
    } catch (error) {
      message.error('Birim seti silinirken hata oluştu');
      console.error('Delete error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUnit = async (id: number) => {
    try {
      await api.delete(`/units/${id}`);
      message.success('Birim silindi');
      loadUnits();
    } catch (error) {
      message.error('Birim silinirken hata oluştu');
      console.error('Delete error:', error);
    }
  };

  const handleSubmitUnitSet = async (values: any) => {
    try {
      setLoading(true);
      if (editingUnitSet) {
        await api.put(`/unitsets/${editingUnitSet.id}`, { id: editingUnitSet.id, ...values });
        message.success('Birim seti güncellendi');
      } else {
        await api.post('/unitsets', values);
        message.success('Birim seti eklendi');
      }
      setModalVisible(false);
      form.resetFields();
      loadUnitSets();
    } catch (error) {
      message.error('Birim seti kaydedilirken hata oluştu');
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitUnit = async (values: any) => {
    try {
      const backendPayload = {
        name: values.unitName,
        symbol: values.unitCode,
        baseRatio: values.isBaseUnit ? 1 : (values.conversionFactor || 1),
        unitSetId: selectedUnitSetId || values.unitSetId
      };

      if (editingUnit) {
        await api.put(`/units/${editingUnit.id}`, { id: editingUnit.id, ...backendPayload });
        message.success('Birim güncellendi');
      } else {
        await api.post('/units', backendPayload);
        message.success('Birim eklendi');
      }
      setUnitModalVisible(false);
      unitForm.resetFields();
      loadUnits();
    } catch (error) {
      message.error('Birim kaydedilirken hata oluştu');
      console.error('Save error:', error);
    }
  };

  const unitColumns: ColumnsType<Unit> = [
    {
      title: 'Birim Kodu',
      dataIndex: 'unitCode',
      key: 'unitCode',
    },
    {
      title: 'Birim Adı',
      dataIndex: 'unitName',
      key: 'unitName',
    },
    {
      title: 'Temel Birim',
      dataIndex: 'isBaseUnit',
      key: 'isBaseUnit',
      render: (isBaseUnit: boolean) => (
        <Tag color={isBaseUnit ? 'blue' : 'default'}>
          {isBaseUnit ? 'Evet' : 'Hayır'}
        </Tag>
      ),
    },
    {
      title: 'Çevrim Katsayısı',
      dataIndex: 'conversionFactor',
      key: 'conversionFactor',
      render: (factor: number) => factor ? factor.toFixed(2) : '-',
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean, record: Unit) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleActiveUnit(record.id, isActive)}
          checkedChildren="Aktif"
          unCheckedChildren="Pasif"
          size="small"
        />
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditUnit(record)}
          />
          <Popconfirm
            title="Bu birimi silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDeleteUnit(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const expandedRowRender = (record: UnitSet) => {
    const unitSetUnits = units.filter(u => u.unitSetId === record.id);
    
    return (
      <div style={{ marginLeft: 40 }}>
        <div style={{ marginBottom: 16 }}>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => handleAddUnit(record.id)}
            size="small"
          >
            Birim Ekle
          </Button>
        </div>
        <Table
          columns={unitColumns}
          dataSource={unitSetUnits}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </div>
    );
  };

  const columns: ColumnsType<UnitSet> = [
    {
      title: 'Birim Seti Adı',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Birim Sayısı',
      key: 'unitCount',
      render: (_, record) => {
        const count = units.filter(u => u.unitSetId === record.id).length;
        return <Tag color="cyan">{count} birim</Tag>;
      },
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean, record: UnitSet) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleActiveUnitSet(record.id, isActive)}
          checkedChildren="Aktif"
          unCheckedChildren="Pasif"
        />
      ),
    },
    {
      title: 'Oluşturulma Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditUnitSet(record)}
          />
          <Popconfirm
            title="Bu birim setini silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDeleteUnitSet(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Birim Setleri ve Birimler"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddUnitSet}>
            Yeni Birim Seti
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={unitSets}
          rowKey="id"
          loading={loading}
          expandable={{
            expandedRowRender,
            defaultExpandedRowKeys: unitSets.length > 0 ? [unitSets[0].id] : [],
          }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
          }}
        />
      </Card>

      {/* UnitSet Modal */}
      <Modal
        title={editingUnitSet ? 'Birim Seti Düzenle' : 'Yeni Birim Seti'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmitUnitSet}
        >
          <Form.Item
            name="name"
            label="Birim Seti Adı"
            rules={[{ required: true, message: 'Birim seti adı giriniz' }]}
          >
            <Input placeholder="Birim seti adı" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Açıklama"
          >
            <Input.TextArea placeholder="Birim seti açıklaması" rows={3} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingUnitSet ? 'Güncelle' : 'Kaydet'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Unit Modal */}
      <Modal
        title={editingUnit ? 'Birim Düzenle' : 'Yeni Birim'}
        open={unitModalVisible}
        onCancel={() => setUnitModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={unitForm}
          layout="vertical"
          onFinish={handleSubmitUnit}
        >
          <Form.Item
            name="unitCode"
            label="Birim Kodu"
            rules={[{ required: true, message: 'Birim kodu giriniz' }]}
          >
            <Input placeholder="Örn: KG, ADET, M" />
          </Form.Item>

          <Form.Item
            name="unitName"
            label="Birim Adı"
            rules={[{ required: true, message: 'Birim adı giriniz' }]}
          >
            <Input placeholder="Örn: Kilogram, Adet, Metre" />
          </Form.Item>

          <Form.Item
            name="isBaseUnit"
            label="Temel Birim"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="conversionFactor"
            label="Çevrim Katsayısı"
            rules={[{ required: true, message: 'Çevrim katsayısı giriniz' }]}
            tooltip="Temel birime göre çevrim oranı (Temel birim için 1.00)"
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={0.01}
              precision={2}
              placeholder="1.00"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingUnit ? 'Güncelle' : 'Kaydet'}
              </Button>
              <Button onClick={() => setUnitModalVisible(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UnitSets;
